import { z } from 'zod';

import { chatUiBase } from '../../shared/schema/base.js';

/**
 * O desfecho do pedido de UM checkout, como o turno da reserva o diz (#5360).
 * `requested`: o pedido entrou (a reserva existe ou está a caminho no
 * fornecedor). `indeterminate`: o fornecedor não confirmou nem recusou — a
 * reserva pode existir, então nada deve oferecer tentar de novo.
 * `pending_approval` (#6699): o pedido entrou e espera o gestor aprovar —
 * desfecho neutro, nem sucesso nem falha.
 */
export const CheckoutOutcomeSignalSchema = z.enum([
  'requested',
  'indeterminate',
  'pending_approval',
]);
export type CheckoutOutcomeSignal = z.infer<typeof CheckoutOutcomeSignalSchema>;

/**
 * O que o turno da recusa oferece em seguida (#5514): `new_checkout` — o
 * agente emitiu, no MESMO turno, um `product_checkout` novo, pronto para
 * confirmar; `none` — não há nova tentativa no card, e a resposta em texto
 * explica o caminho (outra oferta, nova busca, falar com a equipe).
 */
export const CheckoutRetrySchema = z.enum(['new_checkout', 'none']);
export type CheckoutRetry = z.infer<typeof CheckoutRetrySchema>;

/**
 * Por que o fornecedor (ou a VOLL antes dele) recusou — um código fechado e
 * curto, para telemetria e para a copy do host; a explicação para a pessoa é a
 * resposta em texto. Só recusas: nada foi reservado (o «pode existir» é o
 * `indeterminate`).
 * - `price_changed`: o preço ou a tarifa mudou na hora de reservar.
 * - `offer_unavailable`: a oferta saiu (esgotou, a busca venceu no fornecedor).
 * - `traveler_rejected`: o fornecedor recusou o dado do viajante (passaporte
 *   vencido, nome longo demais).
 * - `billing_rejected`: o centro de custo ou o crédito foi recusado.
 * - `occupancy_unsupported`: o quarto não comporta os hóspedes.
 * - `provider_error`: o fornecedor falhou sem um motivo que se saiba.
 */
export const CheckoutFailureReasonSchema = z.enum([
  'price_changed',
  'offer_unavailable',
  'traveler_rejected',
  'billing_rejected',
  'occupancy_unsupported',
  'provider_error',
]);
export type CheckoutFailureReason = z.infer<typeof CheckoutFailureReasonSchema>;

const outcomeBase = {
  ...chatUiBase,
  kind: z.literal('checkout_outcome'),
  challengeId: z.string().min(1),
};

const RecordedOutcomeSchema = z
  .object({
    ...outcomeBase,
    outcome: CheckoutOutcomeSignalSchema,
    tripId: z.string().min(1).optional(),
  })
  .strict();

/** A recusa explícita (#5514): sem ela o card só podia deduzir a falha do
 *  turno que acabou sem desfecho — e dizia o mesmo no erro de rede. Sem
 *  `tripId`: nada entrou na viagem. */
const FailedOutcomeSchema = z
  .object({
    ...outcomeBase,
    outcome: z.literal('failed'),
    retry: CheckoutRetrySchema,
    reason: CheckoutFailureReasonSchema.optional(),
  })
  .strict();

/**
 * Membro `checkout_outcome` do `ChatUiSchema` — o desfecho mínimo do checkout
 * (#5360), DATA-ONLY. Sai no turno da reserva sempre que o bloco cheio
 * (`*_next_services`) não sai: sem serviços a sugerir, erro ao montá-lo, a
 * reserva indeterminada ou a recusa (`failed`, #5514). Leva o `challengeId` do
 * checkout, que é a única chave entre o card que enviou e o turno que
 * respondeu — inclusive no replay do histórico, onde não há envio em memória.
 * Com `tripId`, também é o sinal «entrou na viagem».
 */
export const CheckoutOutcomeBlockSchema = z.discriminatedUnion('outcome', [
  RecordedOutcomeSchema,
  FailedOutcomeSchema,
]);
export type CheckoutOutcomeBlock = z.infer<typeof CheckoutOutcomeBlockSchema>;
